/**
 * 上传全屏遮罩（独立于 vant Toast 实现）
 *
 * 为什么不用 Toast.loading：
 * vant 的 Toast 是单例组件，newUpload.vue 的上传流程中在 loading 之后还会调用
 * Toast.success（如"开始分块上传..."），后弹出的 Toast 会直接顶掉 duration:0 的
 * loading，导致遮罩在分块上传期间消失、用户仍可点击表单提交按钮。
 * 因此这里用独立的 DOM 遮罩，生命周期完全由本模块控制，不受任何 Toast 干扰。
 *
 * 采用引用计数：多个上传链路可以并发开启，计数归零才真正移除遮罩，
 * 避免 A 链路结束时把 B 链路的遮罩提前关掉。
 */

const MASK_CLASS = 'upload-block-mask';
const MASK_ID = 'upload-block-mask-root';
// 提交按钮 z-index 为 999，这里取更高值确保完全覆盖
const MASK_Z_INDEX = 3000;
// 超时兜底（毫秒）：极端情况下上传流程未走到 finally 时，强制解除遮罩，
// 避免遮罩永不消失把整个页面锁死
const AUTO_RELEASE_TIMEOUT = 10 * 60 * 1000;

let maskEl = null;
let refCount = 0;
let autoReleaseTimer = null;

function ensureMaskElement(text) {
  if (maskEl && document.body.contains(maskEl)) {
    return maskEl;
  }
  const el = document.createElement('div');
  el.id = MASK_ID;
  el.className = MASK_CLASS;
  el.setAttribute('data-upload-block-mask', 'true');
  // 屏蔽遮罩上的一切触摸/点击事件，穿透到下方表单提交按钮
  el.addEventListener('touchmove', (e) => e.preventDefault(), { passive: false });
  el.addEventListener('click', (e) => e.stopPropagation(), true);
  el.innerHTML = `
    <div class="${MASK_CLASS}__box">
      <div class="${MASK_CLASS}__spinner"></div>
      <div class="${MASK_CLASS}__text"></div>
    </div>
  `;
  document.body.appendChild(el);
  maskEl = el;
  updateMaskText(text);
  return el;
}

function updateMaskText(text) {
  if (!maskEl) return;
  const textEl = maskEl.querySelector(`.${MASK_CLASS}__text`);
  if (textEl && text) {
    textEl.textContent = text;
  }
}

function clearAutoReleaseTimer() {
  if (autoReleaseTimer) {
    clearTimeout(autoReleaseTimer);
    autoReleaseTimer = null;
  }
}

/**
 * 开启全屏遮罩（引用计数 +1）
 * @param {string} text 遮罩上展示的文案
 * @param {number} autoReleaseMs 超时兜底时间，默认 10 分钟
 */
export function showUploadBlockMask(text = '文件上传中，请勿离开页面', autoReleaseMs = AUTO_RELEASE_TIMEOUT) {
  refCount += 1;
  ensureMaskElement(text);
  // 记录页面原有 overflow，锁定背景滚动
  if (!document.body.dataset.uploadMaskPrevOverflow) {
    document.body.dataset.uploadMaskPrevOverflow = document.body.style.overflow || '';
  }
  document.body.style.overflow = 'hidden';
  clearAutoReleaseTimer();
  autoReleaseTimer = setTimeout(() => {
    forceClearUploadBlockMask();
  }, autoReleaseMs);
}

/**
 * 关闭全屏遮罩（引用计数 -1，归零才真正移除）
 */
export function hideUploadBlockMask() {
  refCount = Math.max(0, refCount - 1);
  if (refCount > 0) return;
  forceClearUploadBlockMask();
}

/**
 * 无条件清除遮罩（组件销毁、异常兜底时使用）
 */
export function forceClearUploadBlockMask() {
  clearAutoReleaseTimer();
  refCount = 0;
  if (maskEl && maskEl.parentNode) {
    maskEl.parentNode.removeChild(maskEl);
  }
  maskEl = null;
  if (document.body.style.overflow === 'hidden') {
    document.body.style.overflow = document.body.dataset.uploadMaskPrevOverflow || '';
  }
  delete document.body.dataset.uploadMaskPrevOverflow;
}

/**
 * 更新遮罩文案（例如压缩阶段、上传阶段展示不同提示）
 * @param {string} text
 */
export function setUploadBlockMaskText(text) {
  updateMaskText(text);
}

/**
 * 当前遮罩是否处于展示状态
 */
export function isUploadBlockMaskVisible() {
  return refCount > 0 && !!maskEl && document.body.contains(maskEl);
}

/**
 * 注入遮罩样式（仅注入一次）
 * 样式内联在 JS 中，避免依赖构建配置对 less/css 的额外处理
 */
function injectMaskStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('upload-block-mask-style')) return;
  const style = document.createElement('style');
  style.id = 'upload-block-mask-style';
  style.textContent = `
.${MASK_CLASS} {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: ${MASK_Z_INDEX};
  background-color: rgba(0, 0, 0, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
}
.${MASK_CLASS}__box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 24px;
  border-radius: 8px;
  background-color: rgba(0, 0, 0, 0.75);
  max-width: 80%;
}
.${MASK_CLASS}__spinner {
  width: 36px;
  height: 36px;
  margin-bottom: 12px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: upload-block-mask-spin 0.8s linear infinite;
}
.${MASK_CLASS}__text {
  color: #fff;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  word-break: break-all;
}
@keyframes upload-block-mask-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;
  document.head.appendChild(style);
}

injectMaskStyle();
